/* charts.js — Chart.js setup for the by-day stacked bar charts. */
import Chart from 'chart.js/auto';

(function () {
  'use strict';
  const CAD = (window.CAD = window.CAD || {});

  function available() { return typeof Chart !== 'undefined'; }

  function categoryMap(taxonomy) {
    const map = {};
    ((taxonomy && taxonomy.categories) || []).forEach(function (c) { map[c.id] = c; });
    return map;
  }

  function orderedCategories(taxonomy) {
    const cats = (taxonomy && taxonomy.categories) || [];
    const order = (taxonomy && taxonomy.matching && taxonomy.matching.priorityOrder) || [];
    const out = [];
    const seen = new Set();
    order.forEach(function (id) {
      const cat = cats.find(function (c) { return c.id === id; });
      if (cat && !seen.has(cat.id)) { out.push(cat); seen.add(cat.id); }
    });
    cats.forEach(function (cat) { if (!seen.has(cat.id)) { out.push(cat); seen.add(cat.id); } });
    return out;
  }

  function dayStart(d) { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; }

  function buildDayList(range, files) {
    let start = range.start ? dayStart(range.start) : null;
    const end = dayStart(range.end);
    if (!start) {
      // "All synced data": derive the left edge from the earliest record.
      start = end;
      for (let i = 0; i < files.length; i++) {
        const t = dayStart(new Date(files[i].createdAt));
        if (t < start) start = t;
      }
    }
    const days = [];
    const cursor = new Date(start);
    while (cursor <= end && days.length < 400) {
      days.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    return days;
  }

  function dayKey(d) {
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  }

  /**
   * Render a stacked by-day bar chart of `files` onto `canvas`.
   * Destroys any chart previously bound to that canvas. Returns the Chart
   * instance, or null when the Chart.js CDN failed to load.
   */
  function renderStackedByDay(canvas, files, range, taxonomy) {
    if (canvas._cadChart) { canvas._cadChart.destroy(); canvas._cadChart = null; }
    if (!available()) return null;

    const cats = orderedCategories(taxonomy);
    const days = buildDayList(range, files);
    const keySet = new Set(days.map(dayKey));
    const buckets = {};
    days.forEach(function (d) { buckets[dayKey(d)] = {}; });

    files.forEach(function (f) {
      const t = new Date(f.createdAt);
      if (isNaN(t.getTime())) return;
      if (range.start && t < range.start) return;
      if (t > range.end) return;
      const key = dayKey(dayStart(t));
      if (!keySet.has(key)) return;
      buckets[key][f.categoryId] = (buckets[key][f.categoryId] || 0) + 1;
    });

    const present = cats.filter(function (cat) {
      return days.some(function (d) { return (buckets[dayKey(d)][cat.id] || 0) > 0; });
    });

    const datasets = present.map(function (cat) {
      return {
        label: cat.label,
        data: days.map(function (d) { return buckets[dayKey(d)][cat.id] || 0; }),
        backgroundColor: cat.color,
        stack: 'files',
        borderRadius: 2,
        barPercentage: 0.85,
      };
    });

    canvas._cadChart = new Chart(canvas, {
      type: 'bar',
      data: {
        labels: days.map(function (d) { return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); }),
        datasets: datasets,
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 200 },
        scales: {
          x: { stacked: true, grid: { display: false }, ticks: { maxRotation: 0, autoSkipPadding: 12 } },
          y: { stacked: true, beginAtZero: true, ticks: { precision: 0 } },
        },
        plugins: {
          legend: { position: 'bottom', labels: { boxWidth: 10, boxHeight: 10, usePointStyle: true, pointStyle: 'rectRounded', font: { size: 11 } } },
          tooltip: { mode: 'index' },
        },
      },
    });
    return canvas._cadChart;
  }

  function destroyOn(canvas) {
    if (canvas && canvas._cadChart) { canvas._cadChart.destroy(); canvas._cadChart = null; }
  }

  CAD.charts = {
    available: available,
    renderStackedByDay: renderStackedByDay,
    destroyOn: destroyOn,
    orderedCategories: orderedCategories,
    categoryMap: categoryMap,
  };
})();
